import moment from 'moment';
import config from '@/config';

const namespaced = true

// Helper function to create indexed data structures for fast lookups
const createDataIndexes = (data) => {
    return {
        sessionTypesMap: new Map(data['sessiontypes']?.map(t => [t.id, t]) || []),
        tagsMap: new Map(data['tags']?.map(t => [t.id, t]) || []),
        locationsMap: new Map(data['locations']?.map(l => [l.id, l]) || []),
        personsMap: new Map(data['persons']?.map(p => [p.id, p]) || []),
        presentationsMap: new Map(data['presentations']?.map(p => [p.id, p]) || []),
        
        // Group links by session_id
        typeLinksMap: (() => {
            const map = new Map();
            const links = data['session_sessiontype_links'] || [];
            for (let i = 0; i < links.length; i++) {
                map.set(links[i].session_id, links[i]);
            }
            return map;
        })(),
        
        tagLinksMap: (() => {
            const map = new Map();
            const links = data['session_tag_links'] || [];
            for (let i = 0; i < links.length; i++) {
                map.set(links[i].session_id, links[i]);
            }
            return map;
        })(),
        
        locationLinksMap: (() => {
            const map = new Map();
            const links = data['session_location_links'] || [];
            for (let i = 0; i < links.length; i++) {
                map.set(links[i].session_id, links[i]);
            }
            return map;
        })(),
        
        sessPresentationLinksMap: (() => {
            const map = new Map();
            const links = data['session_presentation_links'] || [];
            for (let i = 0; i < links.length; i++) {
                const link = links[i];
                if (!map.has(link.session_id)) {
                    map.set(link.session_id, []);
                }
                map.get(link.session_id).push(link);
            }
            return map;
        })(),
        
        sessionRolesMap: (() => {
            const map = new Map();
            const roles = data['person_roles'] || [];
            for (let i = 0; i < roles.length; i++) {
                const role = roles[i];
                if (role.session_id) {
                    if (!map.has(role.session_id)) {
                        map.set(role.session_id, []);
                    }
                    map.get(role.session_id).push(role);
                }
            }
            return map;
        })(),
        
        presRolesMap: (() => {
            const map = new Map();
            const roles = data['person_roles'] || [];
            for (let i = 0; i < roles.length; i++) {
                const role = roles[i];
                if (role.presentation_id) {
                    if (!map.has(role.presentation_id)) {
                        map.set(role.presentation_id, []);
                    }
                    map.get(role.presentation_id).push(role);
                }
            }
            return map;
        })()
    };
};

const state = {
    activeSessions: [],
    joinedChannel: null,
    recommendationOptions: {
        "Session type": { // RECOMMENDATION SYSTEM NAME
            "weighting": 3, // wieght ( over 5)
            "identifier": 'session_type_text' //SESSION OBJECT NAME
        },
        "Chair": {
            "weighting": 3,
            "identifier": 'chair'
        },
    },
    recommendationLength : 20
}

const actions = {
    async getAllSessions({dispatch}) {
        return await dispatch('database/getItems', {table: 'sessions'}, {root: true})
    },
    async getSingleSession({dispatch}, id) {
        //return await dispatch('database/getItem', {table: 'sessions', id: id}, {root: true})
        let data = await dispatch('database/getMultiple', ['sessions', 'locations', 'session_location_links',], {root: true})
        let session = data['sessions'].find(s => s.id == id);
        //location-block
        let sessloc_link = data['session_location_links'].filter(sessloc_link => sessloc_link.session_id == session.id);

        let location = null;
        sessloc_link.forEach(sessloc => {
            if (sessloc && sessloc.location_id) {
                location = data['locations'].filter(location => location.id == sessloc.location_id)[0];
            }
        })
        session.location = location;
        return session;
    },
    async getSingleSessionForPresId({dispatch}, id) {
        //return await dispatch('database/getItem', {table: 'sessions', id: id}, {root: true})
        let data = await dispatch('database/getMultiple', ['session_presentation_links', 'sessions', 'locations', 'session_location_links',], {root: true})
        let sesspreslink = data['session_presentation_links'].find(spl => spl.presentation_id == id)
        let session = data['sessions'].find(s => s.id == sesspreslink.session_id);
        //location-block
        let sessloc_link = data['session_location_links'].filter(sessloc_link => sessloc_link.session_id == session.id);

        let location = null;
        sessloc_link.forEach(sessloc => {
            if (sessloc && sessloc.location_id) {
                location = data['locations'].filter(location => location.id == sessloc.location_id)[0];
            }
        })
        session.location = location;
        return session;
    },


    async getSessionType({dispatch}, id) {
        let data = await dispatch('database/getMultiple', ['sessiontypes', 'session_sessiontype_links'], {root: true});
        let typelink = data['session_sessiontype_links'].find(ssl => ssl.session_id == id);
        let type = null;
        if (typelink) {
            type = data['sessiontypes'].find(st => st.id == typelink.sessiontype_id);
        }
        return type;

    },
    
    // Optimized single action for SessionDetail.vue - fetches all needed data in one database call
    async getSessionDetailData({dispatch}, id) {
        const start = Date.now();
        
        // Single database call for all data
        let data = await dispatch('database/getMultiple', [
            'sessions', 
            'locations', 
            'session_location_links',
            'sessiontypes',
            'session_sessiontype_links',
            'person_roles',
            'persons',
            'session_presentation_links',
            'presentations'
        ], {root: true});
        
        // Create indexes for fast lookups
        const personsMap = new Map(data['persons'].map(p => [p.id, p]));
        const locationsMap = new Map(data['locations'].map(l => [l.id, l]));
        const sessionTypesMap = new Map(data['sessiontypes'].map(t => [t.id, t]));
        const presentationsMap = new Map(data['presentations'].map(p => [p.id, p]));
        
        // Get session
        let session = data['sessions'].find(s => s.id == id);
        if (!session) return null;
        
        // Get location - O(1) lookup
        let sessloc_link = data['session_location_links'].find(link => link.session_id == session.id);
        session.location = sessloc_link ? locationsMap.get(sessloc_link.location_id) : null;
        
        // Get session type - O(1) lookup
        let typelink = data['session_sessiontype_links'].find(link => link.session_id == id);
        let sessiontype = typelink ? sessionTypesMap.get(typelink.sessiontype_id) : null;
        
        // Get persons (chairs/moderators) 
        let persons = [];
        let personIds = new Set();
        const sessionRoles = data['person_roles'].filter(role => role.session_id == id);
        for (let i = 0; i < sessionRoles.length; i++) {
            const role = sessionRoles[i];
            let person = personsMap.get(role.person_id);
            if (person && !personIds.has(person.id)) {
                persons.push({
                    ...person,
                    order: role.order,
                    function: role.function,
                    coi: role.person_roles
                });
                personIds.add(person.id);
            }
        }
        
        // Get speakers - optimized with Map grouping
        const presRolesMap = new Map();
        const presRoles = data['person_roles'].filter(role => role.presentation_id && !role.session_id);
        for (let i = 0; i < presRoles.length; i++) {
            const role = presRoles[i];
            if (!presRolesMap.has(role.presentation_id)) {
                presRolesMap.set(role.presentation_id, []);
            }
            presRolesMap.get(role.presentation_id).push(role);
        }
        
        let speakers = [];
        let speakerIds = new Set();
        const sessionPresLinks = data['session_presentation_links'].filter(link => link.session_id === id);
        for (let i = 0; i < sessionPresLinks.length; i++) {
            const sess_pres_link = sessionPresLinks[i];
            let roles = presRolesMap.get(sess_pres_link.presentation_id) || [];
            for (let j = 0; j < roles.length; j++) {
                const role = roles[j];
                let person = personsMap.get(role.person_id);
                if (person && !speakerIds.has(person.id)) {
                    speakers.push({
                        ...person,
                        function: role.function
                    });
                    speakerIds.add(person.id);
                }
            }
        }
        
        // Get presentations 
        const presentations = [];
        const presentationLinks = data['session_presentation_links'].filter(link => link.session_id === id);
        for (let i = 0; i < presentationLinks.length; i++) {
            const sess_pres_link = presentationLinks[i];
            const presentation = presentationsMap.get(sess_pres_link.presentation_id);
            
            if (!presentation) continue;
            
            const result = {
                ...sess_pres_link,
                color: '#fff',
                speakers: [],
                title: presentation.title,
                pres_id: presentation.id,
                outline: presentation.outline,
                video_link: presentation.video_link,
                slides_id: presentation.slides_id
            };
            
            const roles = presRolesMap.get(presentation.id) || [];
            const presPersonIds = new Set();
            
            for (let j = 0; j < roles.length; j++) {
                const role = roles[j];
                const person = personsMap.get(role.person_id);
                if (person && !presPersonIds.has(person.id)) {
                    result.speakers.push({
                        ...person,
                        function: role.function,
                        coi: role.person_roles
                    });
                    presPersonIds.add(person.id);
                }
            }
            
            presentations.push(result);
        }
        presentations.sort((a, b) => a.order - b.order);
        
      
        return {
            session,
            sessiontype,
            persons: persons.sort((a, b) => a.order - b.order),
            speakers: speakers.sort((a, b) => a.order - b.order),
            presentations
        };
    },
    async getRecommendedSessionList({dispatch}, payload) {
        const { favoriteSessions, excludedSessionTypes } = payload || {};
        
        // Get all sessions
        let data = await dispatch('database/getMultiple', ['sessions', 'sessiontypes', 'session_sessiontype_links', 'locations', 'session_location_links', 'person_roles', 'persons', 'session_presentation_links', 'tags','session_tag_links'], {root: true});
        
        const indexes = createDataIndexes(data);
        
        // Build complete session list
        let allSessions = [];
        for (let i = 0; i < data['sessions'].length; i++) {
            let session = data['sessions'][i];
            if (session.parent_id) continue;
            
            let sesstype_link = indexes.typeLinksMap.get(session.id);
            let type = sesstype_link ? indexes.sessionTypesMap.get(sesstype_link.sessiontype_id) : null;
            
            let tag_link = indexes.tagLinksMap.get(session.id);
            let tag = tag_link ? indexes.tagsMap.get(tag_link.tag_id) : null;
            
            session.session_topic_text = tag ? tag.title : '';
            session.session_type_text = type ? type.name : '';
            session.session_type_order = type ? type.order : 0;
            session.is_scientific = type ? type.scientific : true;
            
            let sessloc_link = indexes.locationLinksMap.get(session.id);
            let location = sessloc_link ? indexes.locationsMap.get(sessloc_link.location_id) : null;
            session.location = location;
            session.location_order = location ? location.order : 0;
            
            if (session.is_scientific) {
                session.chair = [];
                session.speakers = [];
                
                const chairIds = new Set();
                const speakerNames = new Set();
                
                let sess_pres_links = indexes.sessPresentationLinksMap.get(session.id) || [];
                
                if (sess_pres_links.length > 0) {
                    for (let j = 0; j < sess_pres_links.length; j++) {
                        const sess_pres_link = sess_pres_links[j];
                        let sessionroles = indexes.sessionRolesMap.get(session.id) || [];
                        let presroles = indexes.presRolesMap.get(sess_pres_link.presentation_id) || [];
                        
                        for (let k = 0; k < sessionroles.length; k++) {
                            const role = sessionroles[k];
                            let person = indexes.personsMap.get(role.person_id);
                            if (person && !chairIds.has(person.id) && role.function !== 'Attendee') {
                                session.chair.push({...person, function: role.function});
                                chairIds.add(person.id);
                            }
                        }
                        
                        for (let k = 0; k < presroles.length; k++) {
                            const role = presroles[k];
                            let person = indexes.personsMap.get(role.person_id);
                            if (person && !speakerNames.has(person.display_name) && role.function !== 'Attendee') {
                                session.speakers.push({...person, function: role.function});
                                speakerNames.add(person.display_name);
                            }
                        }
                    }
                } else {
                    let sessionroles = indexes.sessionRolesMap.get(session.id) || [];
                    for (let j = 0; j < sessionroles.length; j++) {
                        const role = sessionroles[j];
                        let person = indexes.personsMap.get(role.person_id);
                        if (person && !chairIds.has(person.id) && role.function !== 'Attendee') {
                            session.chair.push({...person, function: role.function});
                            chairIds.add(person.id);
                        }
                    }
                }
                
                allSessions.push(session);
            }
        }
        

        // random shuffle for later //TODO better suggestion algorithm, rn we can show the demo with this.
        const shuffleArray = (array) => {
            const shuffled = [...array];
            for (let i = shuffled.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
            }
            return shuffled;
        };
        // Filter sessions by excluded session types --mostly for break, etc.
        const filteredSessions = allSessions.filter(session => {
     
            if (session.session_type_text && excludedSessionTypes) {
                for (let excludedType of excludedSessionTypes) {
                    if (session.session_type_text.indexOf(excludedType) !== -1) {
                        return false;
                    }
                }
            }
            return true;
        });
        
     
        // if favorite, base recommendations with multi-attribute matching
        if (favoriteSessions && favoriteSessions.length > 0) {
            console.log('🎯 Starting recommendation algorithm with', favoriteSessions.length, 'favorite sessions');
            
            // Get recommendation options from state
            const recOptions = state.recommendationOptions;
            console.log('📋 Recommendation options:', recOptions);
            
            // Helper function to get nested property value (e.g., 'location.id')
            const getNestedValue = (obj, path) => {
                return path.split('.').reduce((current, key) => current?.[key], obj);
            };
            
            // Dynamically extract favorite values for each recommendation type
            const favoriteValues = {};
            for (const [optionKey, config] of Object.entries(recOptions)) {
                const identifier = config.identifier;
                const values = new Set();
                
                console.log(`🔍 Extracting favorite values for "${optionKey}" using identifier: "${identifier}"`);
                
                for (const session of favoriteSessions) {
                    const value = getNestedValue(session, identifier);
                    console.log(`   📊 Session "${session.title}" - ${identifier} value:`, value);
                    // Handle array values (like 'chair')
                    if (Array.isArray(value)) {
                        value.forEach(item => {
                            const itemId = item.id || item;
                            console.log(`      → Array item:`, item, `| Extracted ID:`, itemId);
                            if (itemId) values.add(itemId);
                        });
                    } else if (value) {
                        values.add(value);
                    }
                }
                
                favoriteValues[optionKey] = values;
                console.log(`   ✓ Found ${values.size} unique values for "${optionKey}":`, Array.from(values));
            }
            
            console.log('\n🔄 Scoring', filteredSessions.length, 'filtered sessions...');
            
            // Calculate match score and reasons for each session
            const scoredSessions = filteredSessions.map(session => {
                const reasons = [];
                let score = 0;
                
                // Check each recommendation option dynamically
                for (const [key, data] of Object.entries(recOptions)) {
                    const identifier = data.identifier;
                    const weighting = data.weighting;
                    const favoriteSet = favoriteValues[key];
                    
                    if (favoriteSet.size === 0) continue;
                    
                    const sessionValue = getNestedValue(session, identifier);
                    let hasMatch = false;
                    
                    // Handle array values (like 'chair')
                    if (Array.isArray(sessionValue)) {
                        hasMatch = sessionValue.some(item => {
                            const itemId = item.id || item;
                            const match = favoriteSet.has(itemId);
                      
                            return match;
                        });
                    } else if (sessionValue) {
                        hasMatch = favoriteSet.has(sessionValue);
                    }
                    
                    if (hasMatch) {
                        reasons.push(key);
                        score += weighting;
                    }
                }
                
                return {
                    ...session,
                    recommendationReasons: reasons,
                    matchScore: score
                };
            });
            
            // Remove duplicates first (keep first occurrence by session ID)
            const uniqueSessions = [];
            const seenIds = new Set();
            
            // Create a Set of favorite session IDs for fast lookup
            const favoriteSessionIds = new Set(favoriteSessions.map(fs => fs.id));
            
            console.log("🔍 Filtering already favorited and duplicate sessions...");
            console.log(`   Found ${favoriteSessionIds.size} favorite session IDs to exclude`);
            
            for (let i = 0; i < scoredSessions.length; i++) {
                const session = scoredSessions[i];
                // Skip if already seen or if it's in the favorites
                if (!seenIds.has(session.id) && !favoriteSessionIds.has(session.id)) {
                    uniqueSessions.push(session);
                    seenIds.add(session.id);
                }
            }
            
            console.log(`🔍 Removed ${scoredSessions.length - uniqueSessions.length} duplicate/favorited sessions`);
            
            // Filter only sessions with at least one match
            const matchedSessions = uniqueSessions.filter(session => session.matchScore > 0);
            console.log(`✅ Found ${matchedSessions.length} unique sessions with at least one match`);
            
            // Log score distribution
            const scoreDistribution = {};
            matchedSessions.forEach(session => {
                const score = session.matchScore;
                scoreDistribution[score] = (scoreDistribution[score] || 0) + 1;
            });
            console.log('📊 Score distribution:', scoreDistribution);
            
            // Sort by match score (descending), then by start time
            matchedSessions.sort((a, b) => {
                if (b.matchScore !== a.matchScore) {
                    return b.matchScore - a.matchScore;
                }
                return new Date(a.start_time) - new Date(b.start_time);
            });
            
            // Take top 10 recommendations
            console.log(matchedSessions)
            const topRecommendations = matchedSessions.slice(0, state.recommendationLength); //AKI
            
            console.log(`\n🏆 Tops ${topRecommendations.length} unique recommendations:`);
            topRecommendations.forEach((session, index) => {
                console.log(`   ${index + 1}. [Score: ${session.matchScore}] ${session.title} - Reasons: [${session.recommendationReasons.join(', ')}]`);
            });
            
            return topRecommendations;
        } else {
            // No favorites - return random 10 sessions
            console.log('⚠️ No favorite sessions found, returning random 10 sessions');
            
            const shuffled = shuffleArray([...filteredSessions]);
            const randomSessions = shuffled.slice(0, 10).sort((a, b) => {
                return new Date(a.start_time) - new Date(b.start_time);
            });
            
            console.log('🎲 Random sessions:', randomSessions.map(s => s.title));
            
            return randomSessions;
        }
    },

    async getSessionListLive({dispatch}) {
        let data = await dispatch('database/getMultiple', ['sessions', 'sessiontypes', 'session_sessiontype_links', 'locations', 'session_location_links', 'person_roles', 'persons', 'session_presentation_links', 'tags','session_tag_links'], {root: true})
        
        // Use the helper function to create all indexes
        const indexes = createDataIndexes(data);

        console.log("indexes", indexes);
        
        let sessions = [];
        
        for (let i = 0; i < data['sessions'].length; i++) {
            let session = data['sessions'][i];
            if (session.parent_id) continue; // Skip sessions with parent_id
            
            // Get session type - O(1) lookup
            let sesstype_link = indexes.typeLinksMap.get(session.id);
            let type = sesstype_link ? indexes.sessionTypesMap.get(sesstype_link.sessiontype_id) : null;
            
            // Get tag - O(1) lookup
            let tag_link = indexes.tagLinksMap.get(session.id);
            let tag = tag_link ? indexes.tagsMap.get(tag_link.tag_id) : null;
            
            session.session_topic_text = tag ? tag.title : '';
            session.session_type_text = type ? type.name : '';
            session.session_type_order = type ? type.order : 0;
            session.is_scientific = type ? type.scientific : true;
            
            // Get location - O(1) lookup
            let sessloc_link = indexes.locationLinksMap.get(session.id);
            let location = sessloc_link ? indexes.locationsMap.get(sessloc_link.location_id) : null;
            session.location = location;
            session.location_order = location ? location.order : 0;
            
            if (session.is_scientific) {
                session.chair = [];
                session.speakers = [];
                
                // Use Set for O(1) duplicate checking
                const chairIds = new Set();
                const speakerNames = new Set();
                
                let sess_pres_links = indexes.sessPresentationLinksMap.get(session.id) || [];
                
                if (sess_pres_links.length > 0) {
                    for (let j = 0; j < sess_pres_links.length; j++) {
                        const sess_pres_link = sess_pres_links[j];
                        let sessionroles = indexes.sessionRolesMap.get(session.id) || [];
                        let presroles = indexes.presRolesMap.get(sess_pres_link.presentation_id) || [];
                        
                        for (let k = 0; k < sessionroles.length; k++) {
                            const role = sessionroles[k];
                            let person = indexes.personsMap.get(role.person_id);
                            if (person && !chairIds.has(person.id) && role.function !== 'Attendee') {
                                person = {...person}; // Clone to avoid mutation
                                person.function = role.function;
                                session.chair.push(person);
                                chairIds.add(person.id);
                            }
                        }
                        
                        for (let k = 0; k < presroles.length; k++) {
                            const role = presroles[k];
                            let person = indexes.personsMap.get(role.person_id);
                            if (person && !speakerNames.has(person.display_name) && role.function !== 'Attendee') {
                                person = {...person}; // Clone to avoid mutation
                                person.function = role.function;
                                session.speakers.push(person);
                                speakerNames.add(person.display_name);
                            }
                        }
                    }
                } else {
                    let sessionroles = indexes.sessionRolesMap.get(session.id) || [];
                    for (let j = 0; j < sessionroles.length; j++) {
                        const role = sessionroles[j];
                        let person = indexes.personsMap.get(role.person_id);
                        if (person && !chairIds.has(person.id) && role.function !== 'Attendee') {
                            person = {...person}; // Clone to avoid mutation
                            person.function = role.function;
                            session.chair.push(person);
                            chairIds.add(person.id);
                        }
                    }
                }
                
                sessions.push(session);
            }
        }
        
        return sessions;
    },
    async getSessionListPerType({dispatch}, program_type) {
        let data = await dispatch('database/getMultiple', ['sessions', 'sessiontypes', 'session_sessiontype_links', 'locations', 'session_location_links', 'person_roles', 'persons', 'session_presentation_links'], {root: true})
        
        const indexes = createDataIndexes(data);
        let sessions = [];
        
        for (let i = 0; i < data['sessions'].length; i++) {
            let session = data['sessions'][i];
            if (session.parent_id) continue;
            
            let sesstype_link = indexes.typeLinksMap.get(session.id);
            let type = sesstype_link ? indexes.sessionTypesMap.get(sesstype_link.sessiontype_id) : null;
            
            session.session_type_text = type ? type.name : '';
            session.session_type_order = type ? type.order : 0;
            session.is_scientific = type ? type.scientific : true;
            session.program_type = type ? type.program_type : null;
            
            let sessloc_link = indexes.locationLinksMap.get(session.id);
            let location = sessloc_link ? indexes.locationsMap.get(sessloc_link.location_id) : null;
            session.location = location;
            session.location_order = location ? location.order : 0;
            
            if (session.is_scientific && session.program_type == program_type) {
                session.chair = [];
                session.speakers = [];
                
                const chairIds = new Set();
                const speakerNames = new Set();
                
                let sess_pres_links = indexes.sessPresentationLinksMap.get(session.id) || [];
                
                if (sess_pres_links.length > 0) {
                    for (let j = 0; j < sess_pres_links.length; j++) {
                        const sess_pres_link = sess_pres_links[j];
                        let sessionroles = indexes.sessionRolesMap.get(session.id) || [];
                        let presroles = indexes.presRolesMap.get(sess_pres_link.presentation_id) || [];
                        
                        for (let k = 0; k < sessionroles.length; k++) {
                            const role = sessionroles[k];
                            let person = indexes.personsMap.get(role.person_id);
                            if (person && !chairIds.has(person.id) && role.function !== 'Attendee') {
                                session.chair.push({...person, function: role.function});
                                chairIds.add(person.id);
                            }
                        }
                        
                        for (let k = 0; k < presroles.length; k++) {
                            const role = presroles[k];
                            let person = indexes.personsMap.get(role.person_id);
                            if (person && !speakerNames.has(person.display_name) && role.function !== 'Attendee') {
                                session.speakers.push({...person, function: role.function});
                                speakerNames.add(person.display_name);
                            }
                        }
                    }
                } else {
                    let sessionroles = indexes.sessionRolesMap.get(session.id) || [];
                    for (let j = 0; j < sessionroles.length; j++) {
                        const role = sessionroles[j];
                        let person = indexes.personsMap.get(role.person_id);
                        if (person && !chairIds.has(person.id) && role.function !== 'Attendee') {
                            session.chair.push({...person, function: role.function});
                            chairIds.add(person.id);
                        }
                    }
                }
                
                sessions.push(session);
            }
        }
        
        return sessions;
    },

    async getSessionListIndustryLive({dispatch}) {
        let data = await dispatch('database/getMultiple', ['sessions', 'sessiontypes', 'session_sessiontype_links', 'locations', 'session_location_links'], {root: true})
        //let data = await super.getMultiple(['sessions', 'sessiontypes', 'session_sessiontype_links', 'locations', 'session_location_links', 'person_roles', 'persons', 'session_presentation_links']);
        let sessions = [];
        for (let i = 0; i < data['sessions'].length; i++) {
            let session = data['sessions'][i];
            if (!session.parent_id) {
//sesstype-block
                let sesstype_link = data['session_sessiontype_links'].filter(typelink => typelink.session_id == session.id)[0];
                let type = null;

                if (sesstype_link && sesstype_link.sessiontype_id) {
                    type = data['sessiontypes'].filter(type => type.id == sesstype_link.sessiontype_id)[0];
                }
                session.session_type_text = type ? type.name : '';
                session.session_type_order = type ? type.order : 0;
                session.is_scientific = type ? type.scientific : true;
                //location-block
                let sessloc_link = data['session_location_links'].filter(sessloc_link => sessloc_link.session_id == session.id)[0];
                let location = null;
                if (sessloc_link && sessloc_link.location_id) {

                    location = data['locations'].filter(location => location.id == sessloc_link.location_id)[0];
                }
                session.location = location;

                session.location_order = location ? location.order : 0;
                if (type.id == 'da7ad0c0-3ed1-4500-1463-0b0000000320') {
                    session.chair = []
                    session.speakers = [];
                    sessions.push(session);
                }
            }

        }
        return sessions;
    },


    async getSessionListForDay({dispatch}, day) {
        let data = await dispatch('database/getMultiple', ['sessions', 'sessiontypes', 'session_sessiontype_links', 'locations', 'session_location_links', 'person_roles', 'persons', 'session_presentation_links'], {root: true})
        let sessions = [];
        for (let i = 0; i < data['sessions'].length; i++) {
            let session = data['sessions'][i];
            if (session.start_time.indexOf(day) != -1) {
                let sesstype_link = data['session_sessiontype_links'].filter(typelink => typelink.session_id == session.id)[0];
                let type = null;

                if (sesstype_link && sesstype_link.sessiontype_id) {
                    type = data['sessiontypes'].filter(type => type.id == sesstype_link.sessiontype_id)[0];
                }
                session.session_type_text = type ? type.name : '';
                session.session_type_order = type ? type.order : 0;
                session.is_scientific = type ? type.scientific : true;
                let sessloc_link = data['session_location_links'].filter(sessloc_link => sessloc_link.session_id == session.id)[0];
                let location = null;
                if (sessloc_link && sessloc_link.location_id) {

                    location = data['locations'].filter(location => location.id == sessloc_link.location_id)[0];
                }
                session.location = location;

                session.location_order = location ? location.order : 0;
                if (session.session_type_text.indexOf('(PO)') == -1 && session.session_type_text.indexOf('(PL)') == -1) {
                    session.chair = []
                    session.speakers = [];

                    sessions.push(session);
                }
            }

        }
        return sessions;
    },
    async getSessionListInCollection({dispatch}, collection_id) {
        let data = await dispatch('database/getMultiple', ['sessions', 'sessiontypes', 'session_sessiontype_links', 'locations', 'session_location_links', 'person_roles', 'persons', 'session_presentation_links'], {root: true})
        //let data = await super.getMultiple(['sessions', 'sessiontypes', 'session_sessiontype_links', 'locations', 'session_location_links', 'person_roles', 'persons', 'session_presentation_links']);
        let sessions = [];
        for (let i = 0; i < data['sessions'].length; i++) {
            let session = data['sessions'][i];
            if (session.parent_id == collection_id) {
                //sesstype-block
                let sesstype_link = data['session_sessiontype_links'].filter(typelink => typelink.session_id == session.id)[0];
                let type = null;

                if (sesstype_link && sesstype_link.sessiontype_id) {
                    type = data['sessiontypes'].filter(type => type.id == sesstype_link.sessiontype_id)[0];
                }
                session.session_type_text = type ? type.name : '';
                session.session_type_order = type ? type.order : 0;
                session.is_scientific = type ? type.scientific : true;
                //location-block
                let sessloc_link = data['session_location_links'].filter(sessloc_link => sessloc_link.session_id == session.id)[0];
                let location = null;
                if (sessloc_link && sessloc_link.location_id) {

                    location = data['locations'].filter(location => location.id == sessloc_link.location_id)[0];
                }
                session.location = location;

                session.location_order = location ? location.order : 0;
                if (session.session_type_text.indexOf('(PO)') == -1 && session.session_type_text.indexOf('(PL)') == -1) {
                    session.chair = []
                    session.speakers = [];

                    sessions.push(session);
                }
            }

        }
        return sessions;
    },

      async getSessionFavorite({dispatch}, id) {
        let session = await dispatch('database/getItem', {table: 'sessions', id: id}, {root: true})
        let data = await dispatch('database/getMultiple', ['sessiontypes', 'session_sessiontype_links', 'locations', 'session_location_links', 'person_roles', 'persons', 'session_presentation_links', 'presentations'], {root: true})
        //sesstype-block
        session.fav_type = 'session';
        let presentations = data['presentations'];
        let sesstype_link = data['session_sessiontype_links'].filter(typelink => typelink.session_id == session.id)[0];
        let type = null;

        if (sesstype_link && sesstype_link.sessiontype_id) {
            type = data['sessiontypes'].filter(type => type.id == sesstype_link.sessiontype_id)[0];
        }
        session.session_type_text = type ? type.name : '';
        session.session_type_order = type ? type.order : 0;
        session.is_scientific = type ? type.scientific : true;
        //location-block
        let sessloc_link = data['session_location_links'].filter(sessloc_link => sessloc_link.session_id == session.id)[0];
        let location = null;
        if (sessloc_link && sessloc_link.location_id) {

            location = data['locations'].filter(location => location.id == sessloc_link.location_id)[0];
        }
        session.location = location;
        session.location_name = location ? location.name : config.full_name;

        session.location_order = location ? location.order : 0;
        session.chair = []
        session.speakers = [];
        session.presentations = [];

        let sess_pres_links = data['session_presentation_links'].filter(sess_pres_link => sess_pres_link.session_id == session.id);
        if (sess_pres_links.length > 0) {
            sess_pres_links.forEach(sess_pres_link => {

                let presentation = presentations.find(pres => pres.id == sess_pres_link.presentation_id)

                let sessionroles = data['person_roles'].filter(sessrole => sessrole.session_id == session.id)
                let presroles = data['person_roles'].filter(presrole => presrole.presentation_id == sess_pres_link.presentation_id)

                if (presentation) {
                    let found = session.presentations.find(sp => sp.id == presentation.id);
                    if (!found) {
                        presentation.persons = [];
                        presentation.start_time = sess_pres_link.start_time;
                        presentation.end_time = sess_pres_link.end_time;
                        presentation.code = sess_pres_link.code;
                        presroles.forEach(role => {
                            let person = data['persons'].filter(person => person.id == role.person_id)[0];
                            if (person) {
                                person.function = role.function;

                                let personfound = presentation.persons.find(sp => sp.id == person.id);
                                if (!personfound) {
                                    presentation.persons.push(person);
                                }
                            }
                        })
                        session.presentations.push(presentation);
                    }
                }

                sessionroles.forEach(role => {
                    let person = data['persons'].filter(person => person.id == role.person_id)[0];
                    if (person) {
                        person.function = role.function;
                        if (JSON.stringify(session.chair).indexOf(person.id) == -1) {
                            session.chair.push(person);
                        }
                    }
                })
                presroles.forEach(role => {
                    let person = data['persons'].filter(person => person.id == role.person_id)[0];
                    if (person) {
                        person.function = role.function;
                        if (JSON.stringify(session.speakers).indexOf(person.id) == -1 && JSON.stringify(session.chair).indexOf(person.id) == -1) {
                            session.speakers.push(person);
                        }
                    }
                })
            })
        } else {
            let sessionroles = data['person_roles'].filter(sessrole => sessrole.session_id == session.id)
            sessionroles.forEach(role => {
                let person = data['persons'].filter(person => person.id == role.person_id)[0];
                if (person) {
                    person.function = role.function;
                    if (JSON.stringify(session.chair).indexOf(person.id) == -1) {
                        session.chair.push(person);
                    }
                }
            })
        }

        return session;
    },

    async getSessionFavoriteForList({dispatch}, ids) {
        let data = await dispatch('database/getMultiple', ['sessions', 'sessiontypes', 'session_sessiontype_links', 'locations', 'session_location_links', 'person_roles', 'persons', 'session_presentation_links', 'presentations'], {root: true})
        
        const indexes = createDataIndexes(data);
        let sessions = data['sessions'].filter(sess => ids.indexOf(sess.id) != -1);
        
        console.log('All fav sessions', sessions)
        let sessionsToReturn = [];
        
        for (let i = 0; i < sessions.length; i++) {
            let session = sessions[i];
            session.fav_type = 'session';
            
            let sesstype_link = indexes.typeLinksMap.get(session.id);
            let type = sesstype_link ? indexes.sessionTypesMap.get(sesstype_link.sessiontype_id) : null;
            
            session.session_type_text = type ? type.name : '';
            session.session_type_order = type ? type.order : 0;
            session.is_scientific = type ? type.scientific : true;
            
            let sessloc_link = indexes.locationLinksMap.get(session.id);
            let location = sessloc_link ? indexes.locationsMap.get(sessloc_link.location_id) : null;
            session.location = location;
            session.location_name = location ? location.name : config.full_name;
            session.location_order = location ? location.order : 0;
            
            session.chair = [];
            session.speakers = [];
            session.presentations = [];
            
            const chairIds = new Set();
            const speakerIds = new Set();
            
            let sess_pres_links = indexes.sessPresentationLinksMap.get(session.id) || [];
            
            if (sess_pres_links.length > 0) {
                for (let j = 0; j < sess_pres_links.length; j++) {
                    const sess_pres_link = sess_pres_links[j];
                    let presentation = indexes.presentationsMap.get(sess_pres_link.presentation_id);
                    let sessionroles = indexes.sessionRolesMap.get(session.id) || [];
                    let presroles = indexes.presRolesMap.get(sess_pres_link.presentation_id) || [];
                    
                    if (presentation) {
                        let found = session.presentations.find(sp => sp.id == presentation.id);
                        if (!found) {
                            let presentationCopy = {
                                ...presentation,
                                persons: [],
                                start_time: sess_pres_link.start_time,
                                end_time: sess_pres_link.end_time,
                                code: sess_pres_link.code
                            };
                            
                            const presPersonIds = new Set();
                            for (let k = 0; k < presroles.length; k++) {
                                const role = presroles[k];
                                let person = indexes.personsMap.get(role.person_id);
                                if (person && !presPersonIds.has(person.id)) {
                                    presentationCopy.persons.push({
                                        ...person,
                                        function: role.function
                                    });
                                    presPersonIds.add(person.id);
                                }
                            }
                            session.presentations.push(presentationCopy);
                        }
                    }
                    
                    for (let k = 0; k < sessionroles.length; k++) {
                        const role = sessionroles[k];
                        let person = indexes.personsMap.get(role.person_id);
                        if (person && !chairIds.has(person.id)) {
                            session.chair.push({...person, function: role.function});
                            chairIds.add(person.id);
                        }
                    }
                    
                    for (let k = 0; k < presroles.length; k++) {
                        const role = presroles[k];
                        let person = indexes.personsMap.get(role.person_id);
                        if (person && !speakerIds.has(person.id) && !chairIds.has(person.id)) {
                            session.speakers.push({...person, function: role.function});
                            speakerIds.add(person.id);
                        }
                    }
                }
            } else {
                let sessionroles = indexes.sessionRolesMap.get(session.id) || [];
                for (let j = 0; j < sessionroles.length; j++) {
                    const role = sessionroles[j];
                    let person = indexes.personsMap.get(role.person_id);
                    if (person && !chairIds.has(person.id)) {
                        session.chair.push({...person, function: role.function});
                        chairIds.add(person.id);
                    }
                }
            }
            
            sessionsToReturn.push(session);
        }
        
        return sessionsToReturn;
    },


    async getSessionPersons({dispatch}, id) {
        let data = await dispatch('database/getMultiple', ['person_roles', 'persons'], {root: true});
        const personsMap = new Map(data['persons'].map(p => [p.id, p]));
        
        let persons = [];
        let personIds = new Set();
        
        let roles = data['person_roles'].filter(role => role.session_id == id);
        
        for (let i = 0; i < roles.length; i++) {
            const role = roles[i];
            let person = personsMap.get(role.person_id);
            if (person && !personIds.has(person.id)) {
                persons.push({
                    ...person,
                    order: role.order,
                    function: role.function,
                    coi: role.person_roles
                });
                personIds.add(person.id);
            }
        }
        
        return persons;
    },
    async getSessionPersonsWithoutAttendee({dispatch}, id) {
        let data = await dispatch('database/getMultiple', ['person_roles', 'persons'], {root: true});
        const personsMap = new Map(data['persons'].map(p => [p.id, p]));
        
        let persons = [];
        let roles = data['person_roles'].filter(role => role.session_id == id);
        
        for (let i = 0; i < roles.length; i++) {
            const role = roles[i];
            if (role.function !== 'Attendee') {
                let person = personsMap.get(role.person_id);
                if (person) {
                    persons.push({
                        ...person,
                        order: role.order,
                        function: role.function
                    });
                }
            }
        }
        
        return persons;
    },

    async getSpeakersInSession({dispatch}, id) {
        let data = await dispatch('database/getMultiple', ['session_presentation_links', 'person_roles', 'persons'], {root: true});
        
        const personsMap = new Map(data['persons'].map(p => [p.id, p]));
        const presRolesMap = new Map();
        
        const personRoles = data['person_roles'];
        for (let i = 0; i < personRoles.length; i++) {
            const role = personRoles[i];
            if (role.presentation_id) {
                if (!presRolesMap.has(role.presentation_id)) {
                    presRolesMap.set(role.presentation_id, []);
                }
                presRolesMap.get(role.presentation_id).push(role);
            }
        }
        
        let speakers = [];
        let speakerIds = new Set();
        
        const sessionPresLinks = data['session_presentation_links'].filter(link => link.session_id === id);
        for (let i = 0; i < sessionPresLinks.length; i++) {
            const sess_pres_link = sessionPresLinks[i];
            let roles = presRolesMap.get(sess_pres_link.presentation_id) || [];
            for (let j = 0; j < roles.length; j++) {
                const role = roles[j];
                let person = personsMap.get(role.person_id);
                if (person && !speakerIds.has(person.id)) {
                    speakers.push({
                        ...person,
                        function: role.function
                    });
                    speakerIds.add(person.id);
                }
            }
        }
        
        return speakers;
    },
    async getPresentationList({dispatch}, id) {
        const start = Date.now();

        let data = await dispatch('database/getMultiple', ['session_presentation_links', 'presentations', 'person_roles', 'persons'], {root: true});

        // Create indexed structures for fast lookup
        const presentationsMap = new Map(data['presentations'].map(p => [p.id, p]));
        const personsMap = new Map(data['persons'].map(p => [p.id, p]));
        
        // Group person roles by presentation_id (only non-session roles)
        const presRolesMap = new Map();
        const nonSessionRoles = data['person_roles'].filter(role => role.session_id == null);
        for (let i = 0; i < nonSessionRoles.length; i++) {
            const role = nonSessionRoles[i];
            if (role.presentation_id) {
                if (!presRolesMap.has(role.presentation_id)) {
                    presRolesMap.set(role.presentation_id, []);
                }
                presRolesMap.get(role.presentation_id).push(role);
            }
        }

        // Filter session presentation links for this session ID
        const presentations = [];
        const sessionPresLinks = data['session_presentation_links'].filter(link => link.session_id === id);
        for (let i = 0; i < sessionPresLinks.length; i++) {
            const sess_pres_link = sessionPresLinks[i];
            const presentation = presentationsMap.get(sess_pres_link.presentation_id);
            
            if (!presentation) continue;
            
            // Build the result object
            const result = {
                ...sess_pres_link,
                color: '#fff',
                speakers: [],
                title: presentation.title,
                pres_id: presentation.id,
                outline: presentation.outline,
                video_link: presentation.video_link,
                slides_id: presentation.slides_id
            };
            
            // Get roles for this presentation
            const roles = presRolesMap.get(presentation.id) || [];
            const speakerIds = new Set(); // Track added speakers to avoid duplicates
            
            for (let j = 0; j < roles.length; j++) {
                const role = roles[j];
                const person = personsMap.get(role.person_id);
                if (person && !speakerIds.has(person.id)) {
                    result.speakers.push({
                        ...person,
                        function: role.function,
                        coi: role.person_roles
                    });
                    speakerIds.add(person.id);
                }
            }
            
            presentations.push(result);
        }
        presentations.sort((a, b) => a.order - b.order);

        const end = Date.now();
        console.log('Execution time:', end - start);
        return presentations;
    },

    async getDays({dispatch}) {
        let data = await dispatch('database/getMultiple', ['sessions'], {root: true});

        let sessions = data['sessions'].sort((a, b) => {
            return new Date(a.start_time) - new Date(b.start_time);
        });
        let days = {};
        for (let i = 0; i < sessions.length; i++) {
            let session = sessions[i];
            if (session.start_time) {
                let start = session.start_time.split('T')[0];
                if (!days[start]) {
                    days[start] = {};
                    days[start].label = moment(start).format('ddd, MMM D')
                }

            }
        }
        console.log("data days")
        console.log(days)
        return days;

    },

    async getScientificDays({dispatch}) {
        let data = await dispatch('database/getMultiple', ['sessions', 'sessiontypes'], {root: true});

        let sessions = data['sessions'].sort((a, b) => {
            return new Date(a.start_time) - new Date(b.start_time);
        });
        let days = {};
        for (let i = 0; i < sessions.length; i++) {

            let session = sessions[i];
            if (session.start_time) {
                let start = session.start_time.split('T')[0];
                if (!days[start]) {
                    days[start] = {};
                    days[start].label = moment(start).format('ddd, MMM D')
                }

            }
        }
        console.log("data days")
        console.log(days)
        return days;

    },
    async getScientificDaysPerType({dispatch}, _type) {
        let data = await dispatch('database/getMultiple', ['sessions', 'sessiontypes', 'session_sessiontype_links'], {root: true});

        let sessions = data['sessions'].sort((a, b) => {
            return new Date(a.start_time) - new Date(b.start_time);
        });
        let days = {};
        for (let i = 0; i < sessions.length; i++) {

            let session = sessions[i];
            let sesstype_link = data['session_sessiontype_links'].find(typelink => typelink.session_id == session.id);
            let type = null;

            if (sesstype_link && sesstype_link.sessiontype_id) {
                type = data['sessiontypes'].find(type => type.id == sesstype_link.sessiontype_id);
            }
            session.program_type = type ? type.program_type : null;

            if (session.start_time && session.program_type == _type) {
                let start = session.start_time.split('T')[0];
                if (!days[start]) {
                    days[start] = {};
                    days[start].label = moment(start).format('ddd, MMM D')
                }

            }
        }
        console.log("data days")
        console.log(days)
        return days;

    },

    async getGridData({dispatch}, start) {
        let data = await dispatch('database/getMultiple', ['sessions', 'locations', 'session_location_links', 'sessiontypes', 'session_sessiontype_links'], {root: true});
        let locations = data['locations'];
        let location_links = data['session_location_links'];
        let sessions = data['sessions'];
        let sessiontypes = data['sessiontypes'];
        let session_sessiontype_links = data['session_sessiontype_links'];
        let breaks = [];
        let rooms = [];
        let dayStart = 23; //HARDCODED?
        let dayEnd = 0;
        for (let i = 0; i < locations.length; i++) {
            let location = locations[i];
            if (location.name != 'Media library') {
                let room = {id: location.id, name: location.name, sessions: [], slots: {}, order: location.order};
                for (let j = 0; j < location_links.length; j++) {
                    let link = location_links[j];
                    if (link.location_id == location.id) {
                        for (let k = 0; k < sessions.length; k++) {
                            let session = sessions[k];
                            if (link.session_id == session.id) {
                                if (!session.parent_id) {
                                    if (session.start_time.indexOf(start) != -1) {
                                        for (let l = 0; l < session_sessiontype_links.length; l++) {
                                            let sessTypeLink = session_sessiontype_links[l];
                                            if (sessTypeLink.session_id == session.id) {
                                                for (let m = 0; m < sessiontypes.length; m++) {
                                                    let sessType = sessiontypes[m];
                                                    if (sessTypeLink.sessiontype_id == sessType.id) {
                                                        session.session_type_text = sessType.name;
                                                        session.scientific = sessType.scientific;

                                                    }
                                                }
                                            }
                                        }
                                        console.log('scientific', session.scientific)


                                        let startTmp = session.start_time.split('T')[1];
                                        startTmp = parseInt(startTmp.split(':')[0])
                                        let endTmp = session.end_time.split('T')[1];
                                        endTmp = parseInt(endTmp.split(':')[0])
                                        let timeStart = startTmp
                                        let timeEnd = endTmp
                                        if (timeStart < dayStart) dayStart = timeStart;
                                        if (timeEnd > dayEnd) dayEnd = timeEnd;
                                        if (session.scientific) {
                                            if (session.recurring > 0) {
                                                breaks.push(session);
                                            } else {
                                                room.sessions.push(session);
                                                room.slots[session.start_time + '/' + session.end_time] = room.slots[session.start_time + '/' + session.end_time] || {};
                                                room.slots[session.start_time + '/' + session.end_time]['sessions'] = room.slots[session.start_time + '/' + session.end_time]['sessions'] || []
                                                if (JSON.stringify(room.slots[session.start_time + '/' + session.end_time]['sessions']).indexOf(session.id) == -1) {
                                                    room.slots[session.start_time + '/' + session.end_time]['sessions'].push(session);
                                                }

                                            }
                                        }
                                    }
                                }

                            }
                        }
                    }
                }
                if (room.sessions.length) {
                    rooms.push(room);
                }
            }

        }
        console.log('BREAK', breaks)
        console.log('rooms', rooms)
        return {rooms: rooms, breaks: breaks, dayStart: dayStart, dayEnd: dayEnd};
    },
    async getGridDataPerType({dispatch}, payload) {
        let start = payload.day;
        let _type = payload.type;

        let data = await dispatch('database/getMultiple', ['sessions', 'locations', 'session_location_links', 'sessiontypes', 'session_sessiontype_links'], {root: true});
        let locations = data['locations'];
        let location_links = data['session_location_links'];
        let sessions = data['sessions'];
        let sessiontypes = data['sessiontypes'];
        let session_sessiontype_links = data['session_sessiontype_links'];
        let breaks = [];
        let rooms = [];
        let dayStart = 23; //HARDCODED?
        let dayEnd = 0;
        for (let i = 0; i < locations.length; i++) {
            let location = locations[i];
            if (location.name != 'Media library') {
                let room = {id: location.id, name: location.name, sessions: [], slots: {}, order: location.order};
                for (let j = 0; j < location_links.length; j++) {
                    let link = location_links[j];
                    if (link.location_id == location.id) {
                        for (let k = 0; k < sessions.length; k++) {
                            let session = sessions[k];
                            if (link.session_id == session.id) {
                                if (!session.parent_id) {
                                    if (session.start_time.indexOf(start) != -1) {
                                        for (let l = 0; l < session_sessiontype_links.length; l++) {
                                            let sessTypeLink = session_sessiontype_links[l];
                                            if (sessTypeLink.session_id == session.id) {
                                                for (let m = 0; m < sessiontypes.length; m++) {
                                                    let sessType = sessiontypes[m];
                                                    if (sessTypeLink.sessiontype_id == sessType.id) {
                                                        session.session_type_text = sessType.name;
                                                        session.scientific = sessType.scientific;
                                                        session.program_type = sessType.program_type;

                                                    }
                                                }
                                            }
                                        }
                                        console.log('scientific', session.scientific)


                                        let startTmp = session.start_time.split('T')[1];
                                        startTmp = parseInt(startTmp.split(':')[0])
                                        let endTmp = session.end_time.split('T')[1];
                                        endTmp = parseInt(endTmp.split(':')[0])
                                        let timeStart = startTmp
                                        let timeEnd = endTmp
                                        if (timeStart < dayStart) dayStart = timeStart;
                                        if (timeEnd > dayEnd) dayEnd = timeEnd;
                                        if (session.program_type == _type) {
                                            if (session.recurring > 0) {
                                                breaks.push(session);
                                            } else {
                                                room.sessions.push(session);
                                                room.slots[session.start_time + '/' + session.end_time] = room.slots[session.start_time + '/' + session.end_time] || {};
                                                room.slots[session.start_time + '/' + session.end_time]['sessions'] = room.slots[session.start_time + '/' + session.end_time]['sessions'] || []
                                                if (JSON.stringify(room.slots[session.start_time + '/' + session.end_time]['sessions']).indexOf(session.id) == -1) {
                                                    room.slots[session.start_time + '/' + session.end_time]['sessions'].push(session);
                                                }

                                            }
                                        }
                                    }
                                }

                            }
                        }
                    }
                }
                if (room.sessions.length) {
                    rooms.push(room);
                }
            }

        }
        console.log('BREAK', breaks)
        console.log('rooms', rooms)
        return {rooms: rooms, breaks: breaks, dayStart: dayStart, dayEnd: dayEnd};
    },


    async getSessionsForLocationInSlot({dispatch}, payload) {
        let _location = payload.location;
        let start = payload.start;
        let end = payload.end;
        let data = await dispatch('database/getMultiple', ['sessions', 'sessiontypes', 'session_sessiontype_links', 'locations', 'session_location_links', 'person_roles', 'persons', 'session_presentation_links'], {root: true})
        
        const indexes = createDataIndexes(data);
        let sessions = [];
        
        for (let i = 0; i < data['sessions'].length; i++) {
            let session = data['sessions'][i];
            
            let sesstype_link = indexes.typeLinksMap.get(session.id);
            let type = sesstype_link ? indexes.sessionTypesMap.get(sesstype_link.sessiontype_id) : null;
            
            session.session_type_text = type ? type.name : '';
            session.session_type_order = type ? type.order : 0;
            session.is_scientific = type ? type.scientific : true;
            
            let sessloc_link = indexes.locationLinksMap.get(session.id);
            let location = sessloc_link ? indexes.locationsMap.get(sessloc_link.location_id) : null;
            session.location = location;
            session.location_order = location ? location.order : 0;
            
            if (session.location && session.location.id == _location && session.start_time === start && session.end_time === end) {
                if (session.session_type_text.indexOf('(PO)') == -1 && session.session_type_text.indexOf('(PL)') == -1) {
                    session.chair = [];
                    session.speakers = [];
                    
                    const chairIds = new Set();
                    const speakerIds = new Set();
                    
                    let sess_pres_links = indexes.sessPresentationLinksMap.get(session.id) || [];
                    
                    if (sess_pres_links.length > 0) {
                        for (let j = 0; j < sess_pres_links.length; j++) {
                            const sess_pres_link = sess_pres_links[j];
                            let sessionroles = indexes.sessionRolesMap.get(session.id) || [];
                            let presroles = indexes.presRolesMap.get(sess_pres_link.presentation_id) || [];
                            
                            for (let k = 0; k < sessionroles.length; k++) {
                                const role = sessionroles[k];
                                let person = indexes.personsMap.get(role.person_id);
                                if (person && !chairIds.has(person.id) && role.function !== 'Attendee') {
                                    session.chair.push({...person, function: role.function});
                                    chairIds.add(person.id);
                                }
                            }
                            
                            for (let k = 0; k < presroles.length; k++) {
                                const role = presroles[k];
                                let person = indexes.personsMap.get(role.person_id);
                                if (person && !speakerIds.has(person.id) && !chairIds.has(person.id) && role.function !== 'Attendee') {
                                    session.speakers.push({...person, function: role.function});
                                    speakerIds.add(person.id);
                                }
                            }
                        }
                    } else {
                        let sessionroles = indexes.sessionRolesMap.get(session.id) || [];
                        for (let j = 0; j < sessionroles.length; j++) {
                            const role = sessionroles[j];
                            let person = indexes.personsMap.get(role.person_id);
                            if (person && !chairIds.has(person.id) && role.function !== 'Attendee') {
                                session.chair.push({...person, function: role.function});
                                chairIds.add(person.id);
                            }
                        }
                    }
                    
                    sessions.push(session);
                }
            }
        }
        
        return sessions;
    },
    async getSessionsForLocation({dispatch}, _location) {
        let data = await dispatch('database/getMultiple', ['sessions', 'sessiontypes', 'session_sessiontype_links', 'locations', 'session_location_links', 'person_roles', 'persons', 'session_presentation_links'], {root: true})
        
        const indexes = createDataIndexes(data);
        let sessions = [];
        
        for (let i = 0; i < data['sessions'].length; i++) {
            let session = data['sessions'][i];
            
            let sesstype_link = indexes.typeLinksMap.get(session.id);
            let type = sesstype_link ? indexes.sessionTypesMap.get(sesstype_link.sessiontype_id) : null;
            
            session.session_type_text = type ? type.name : '';
            session.session_type_order = type ? type.order : 0;
            session.is_scientific = type ? type.scientific : true;
            
            let sessloc_link = indexes.locationLinksMap.get(session.id);
            let location = sessloc_link ? indexes.locationsMap.get(sessloc_link.location_id) : null;
            session.location = location;
            session.location_order = location ? location.order : 0;
            
            if (session.location && session.location.id == _location) {
                if (session.session_type_text.indexOf('(PO)') == -1 && session.session_type_text.indexOf('(PL)') == -1) {
                    session.chair = [];
                    session.speakers = [];
                    
                    const chairIds = new Set();
                    const speakerIds = new Set();
                    
                    let sess_pres_links = indexes.sessPresentationLinksMap.get(session.id) || [];
                    
                    if (sess_pres_links.length > 0) {
                        for (let j = 0; j < sess_pres_links.length; j++) {
                            const sess_pres_link = sess_pres_links[j];
                            let sessionroles = indexes.sessionRolesMap.get(session.id) || [];
                            let presroles = indexes.presRolesMap.get(sess_pres_link.presentation_id) || [];
                            
                            for (let k = 0; k < sessionroles.length; k++) {
                                const role = sessionroles[k];
                                let person = indexes.personsMap.get(role.person_id);
                                if (person && !chairIds.has(person.id) && role.function !== 'Attendee') {
                                    session.chair.push({...person, function: role.function});
                                    chairIds.add(person.id);
                                }
                            }
                            
                            for (let k = 0; k < presroles.length; k++) {
                                const role = presroles[k];
                                let person = indexes.personsMap.get(role.person_id);
                                if (person && !speakerIds.has(person.id) && !chairIds.has(person.id) && role.function !== 'Attendee') {
                                    session.speakers.push({...person, function: role.function});
                                    speakerIds.add(person.id);
                                }
                            }
                        }
                    } else {
                        let sessionroles = indexes.sessionRolesMap.get(session.id) || [];
                        for (let j = 0; j < sessionroles.length; j++) {
                            const role = sessionroles[j];
                            let person = indexes.personsMap.get(role.person_id);
                            if (person && !chairIds.has(person.id) && role.function !== 'Attendee') {
                                session.chair.push({...person, function: role.function});
                                chairIds.add(person.id);
                            }
                        }
                    }
                    
                    sessions.push(session);
                }
            }
        }
        
        return sessions;
    },
    async getLocation({dispatch}, id) {
        return await dispatch('database/getItem', {table: 'locations', id: id}, {root: true})
    },
    async getStreamingRooms({dispatch}) {
        let locations = await dispatch('database/getItems', {table: 'locations'}, {root: true})
        let locationsToReturn = locations.filter(l => l.stream_link != null);
        return locationsToReturn
    },

    async getTopics({dispatch}, id) {
        let data = await dispatch('database/getMultiple', ['tags', 'session_tag_links'], {root: true});
        let taglinks = data['session_tag_links'].filter(stl => stl.session_id == id);
        let tags = [];
        for (let i = 0; i < taglinks.length; i++) {
            let link = taglinks[i];
            let tag = data['tags'].find(st => st.id == link.tag_id);
            if (!tags.find(t => t.id == tag.id)) {
                tags.push(tag)
            }
        }
        return tags;
    },

    async getSessionsByTheme({dispatch}, theme) {
        let data = await dispatch('database/getMultiple', ['sessions', 'sessiontypes', 'session_sessiontype_links', 'locations', 'session_location_links', 'person_roles', 'persons', 'session_presentation_links', 'tags', 'session_tag_links'], {root: true});
        
        const indexes = createDataIndexes(data);
        let sessionsToReturn = [];
        let label = '';
        
        let sessions = data['sessions'].filter(session => session.themes[theme]);
        console.log('sessions', sessions)
        
        for (let i = 0; i < sessions.length; i++) {
            const session = sessions[i];
            if (!label) {
                label = session.themes[theme];
            }
            
            let sesstype_link = indexes.typeLinksMap.get(session.id);
            if (sesstype_link) {
                let type = indexes.sessionTypesMap.get(sesstype_link.sessiontype_id);
                if (type) {
                    session.session_type_text = type.name;
                    session.session_type_order = type.order;
                    session.is_scientific = type.scientific;
                }
            }
            
            let sessloc_link = indexes.locationLinksMap.get(session.id);
            let location = sessloc_link ? indexes.locationsMap.get(sessloc_link.location_id) : null;
            session.location = location;
            session.location_order = location ? location.order : 0;
            
            session.chair = [];
            session.speakers = [];
            
            const chairIds = new Set();
            const speakerNames = new Set();
            
            let sess_pres_links = indexes.sessPresentationLinksMap.get(session.id) || [];
            
            if (sess_pres_links.length > 0) {
                for (let j = 0; j < sess_pres_links.length; j++) {
                    const sess_pres_link = sess_pres_links[j];
                    let sessionroles = indexes.sessionRolesMap.get(session.id) || [];
                    let presroles = indexes.presRolesMap.get(sess_pres_link.presentation_id) || [];
                    
                    for (let k = 0; k < sessionroles.length; k++) {
                        const role = sessionroles[k];
                        let person = indexes.personsMap.get(role.person_id);
                        if (person && !chairIds.has(person.id)) {
                            session.chair.push({...person, function: role.function});
                            chairIds.add(person.id);
                        }
                    }
                    
                    for (let k = 0; k < presroles.length; k++) {
                        const role = presroles[k];
                        let person = indexes.personsMap.get(role.person_id);
                        if (person && !speakerNames.has(person.display_name)) {
                            session.speakers.push({...person, function: role.function});
                            speakerNames.add(person.display_name);
                        }
                    }
                }
            } else {
                let sessionroles = indexes.sessionRolesMap.get(session.id) || [];
                for (let j = 0; j < sessionroles.length; j++) {
                    const role = sessionroles[j];
                    let person = indexes.personsMap.get(role.person_id);
                    if (person && !chairIds.has(person.id)) {
                        session.chair.push({...person, function: role.function});
                        chairIds.add(person.id);
                    }
                }
            }
            
            sessionsToReturn.push(session);
        }
        
        return {sessions: sessionsToReturn, label: label};
    },

    async getSessionsByIds({dispatch}, ids) {
        let data = await dispatch('database/getMultiple', ['sessions', 'locations', 'session_location_links', 'sessiontypes', 'session_sessiontype_links'], {root: true});
        let results = [];
        for (let i = 0; i < ids.length; i++) {
            let session = data['sessions'].find(s => s.id === ids[i]);
            if (!session) continue;
            let locLink = data['session_location_links'].find(l => l.session_id === session.id);
            session.location = locLink ? data['locations'].find(l => l.id === locLink.location_id) : null;
            let typeLink = data['session_sessiontype_links'].find(l => l.session_id === session.id);
            session.session_type_text = typeLink ? (data['sessiontypes'].find(t => t.id === typeLink.sessiontype_id) || {}).name || '' : '';
            results.push(session);
        }
        return results;
    },

    async getPresentationsByIds({dispatch}, ids) {
        let data = await dispatch('database/getMultiple', ['presentations', 'sessions', 'session_presentation_links', 'person_roles', 'persons'], {root: true});

        const presentationsMap = new Map(data['presentations'].map(p => [p.id, p]));
        const personsMap = new Map(data['persons'].map(p => [p.id, p]));

        // Group person roles by presentation_id (only non-session roles)
        const presRolesMap = new Map();
        const nonSessionRoles = data['person_roles'].filter(role => role.session_id == null);
        for (let i = 0; i < nonSessionRoles.length; i++) {
            const role = nonSessionRoles[i];
            if (role.presentation_id) {
                if (!presRolesMap.has(role.presentation_id)) {
                    presRolesMap.set(role.presentation_id, []);
                }
                presRolesMap.get(role.presentation_id).push(role);
            }
        }

        let results = [];
        for (let i = 0; i < ids.length; i++) {
            let presentation = presentationsMap.get(ids[i]);
            if (!presentation) continue;

            let presLink = data['session_presentation_links'].find(l => l.presentation_id === presentation.id);
            let session = presLink ? data['sessions'].find(s => s.id === presLink.session_id) : null;

            const result = {
                ...(presLink || {}),
                color: '#fff',
                speakers: [],
                title: presentation.title,
                pres_id: presentation.id,
                outline: presentation.outline,
                video_link: presentation.video_link,
                slides_id: presentation.slides_id,
                session_title: session ? session.title : '',
                session_id: presLink ? presLink.session_id : null
            };

            // Get roles for this presentation
            const roles = presRolesMap.get(presentation.id) || [];
            const speakerIds = new Set();

            for (let j = 0; j < roles.length; j++) {
                const role = roles[j];
                const person = personsMap.get(role.person_id);
                if (person && !speakerIds.has(person.id)) {
                    result.speakers.push({
                        ...person,
                        function: role.function,
                        coi: role.person_roles
                    });
                    speakerIds.add(person.id);
                }
            }

            results.push(result);
        }
        return results;
    },

}
const mutations = {
    setActiveSessions(state, data) {
        let active = [];
        for (let i = 0; i < data.length; i++) {
            const entry = data[i];
            active.push(entry);
        }
        state.activeSessions = active;
    },
    setJoinedChannel: (state, {joinedChannel}) => {
        state.joinedChannel = joinedChannel
    },
}
const getters = {}

export const sessions = {
    namespaced,
    state,
    getters,
    mutations,
    actions
}
